import { useEffect, useRef, type RefObject } from "react";

import { PHONE_QUERY } from "@/agent/chat";

/** Radix popups (menus, popovers, tooltips, selects), each in a wrapper around its content. */
const POPPER = "[data-radix-popper-content-wrapper]";
/** The popups that dismiss themselves on a press outside (a tooltip only follows the hover). */
const DISMISSABLE = "[role=menu], [role=dialog], [role=listbox]";
/** Content above the page, portalled or not: a press there is never outside the lip. */
const POPUP = `${POPPER}, [role=dialog], [role=alertdialog]`;

/** Whether a menu, popover or select is open (not on its way out): it takes the next press. */
function popupOpen(): boolean {
  return [...document.querySelectorAll(POPPER)].some((wrapper) => {
    const content = wrapper.firstElementChild;
    return !!content?.matches(DISMISSABLE) && content.getAttribute("data-state") !== "closed";
  });
}

/** The child of <body> holding `node`: the app's root, or a portal's own container. */
function bodyLayer(node: Node): Node | null {
  let layer: Node | null = node;
  while (layer && layer.parentNode !== node.ownerDocument?.body) layer = layer.parentNode;
  return layer;
}

/**
 * Whether a press on `target` is outside the lip: on the page around it, not on the lip, not on
 * anything layered above the page (the portals the lip opens: the model menu, previews, dialogs,
 * each its own child of <body>), and not on something that may start a drag (a document row
 * dragged into the lip). A modal dialog sets pointer-events: none on <body>, so a press beside it
 * lands on <html>: inside too.
 */
export function isOutsideLip(target: EventTarget | null, lip: HTMLElement): boolean {
  if (!(target instanceof Node) || lip.contains(target)) return false;
  if (target === target.ownerDocument?.documentElement) return false;
  if (target instanceof Element && target.closest(`${POPUP}, [draggable=true]`)) return false;
  return bodyLayer(target) === bodyLayer(lip);
}

/**
 * Closes the open lip when the page around it is pressed (desktop; the phone's open lip is the
 * whole screen). `close` puts the focus where it belongs, and a press that focuses nothing leaves
 * it there. Decided on pointerdown, so a text selection started in the lip and released outside
 * it never closes it (nor does a file drag, which sends no pointerdown). A press while a popup is
 * open only dismisses the popup; one from a second pointer while the handle resizes the lip does
 * nothing. Listens only while open.
 */
export function useCloseOnOutside(
  lip: RefObject<HTMLElement>,
  open: boolean,
  close: () => void
): void {
  const onClose = useRef(close);
  onClose.current = close;

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const element = lip.current;
      if (event.button !== 0 || !element || window.matchMedia(PHONE_QUERY).matches) return;
      if ("dragging" in element.dataset || popupOpen()) return;
      if (!isOutsideLip(event.target, element)) return;
      onClose.current();
      // The press itself moves focus next: onto what it pressed, or nowhere. Nowhere gives the
      // focus back to where closing put it (the bar).
      const focused = document.activeElement;
      requestAnimationFrame(() => {
        if (document.activeElement === document.body && focused instanceof HTMLElement) {
          focused.focus({ preventScroll: true, focusVisible: false });
        }
      });
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [lip, open]);
}
